import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { BlendCheckGate, BlendCheckHint } from 'src/components/blend-check';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Loader from 'src/components/loader';
import LocationsMismatchWarning from 'src/components/LocationsMismatchWarning';
import NotFound from 'src/components/not-found';
import RangeInputConversionTooltip from 'src/components/RangeInputConversionTooltip';
import Input from 'src/components/ui/Input';
import FeaturesContext from 'src/context/FeaturesContext';
import { useBatch } from 'src/hooks/services/useBatch';
import { useModule } from 'src/hooks/services/useContainer';
import useBlendCheck, { blendResource } from 'src/hooks/useBlendCheck';
import useSelectedAmountHandler from 'src/hooks/useSelectedAmountHandler';
import useActionLoadingStore from 'src/stores/useActionLoadingStore';
import useActionPanelStore from "src/stores/useActionPanelStore";
import {
  loadMultipleContainersIntoDestinationAction,
  partialLoadContainerAction,
} from 'src/utils/actionsAPIUtils';
import { relocateContainerAction } from 'src/utils/actionsAPIUtils';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  MATERIAL_BATCH_ACTIONS,
  MATERIAL_CONTAINER_STATUSES,
  PAGINATION_IGNORE_DEFAULT_LIMIT,
} from 'src/utils/constants';
import { convertToUserUnits, formatThreeDecimalsNumber, formatUnits } from 'src/utils/conversions';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import { formatConvertedUnits } from 'src/utils/ui';
import { getRouteURI, getShortUuid, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';
import { getInputStep, hasLocationsMismatch } from 'src/utils/validation';

import routes from '../../../../utils/routes';
import ActionPage from './_action-wrapper';

const SplitBatch = ({ user }) => {
  const [searchParams] = useSearchParams();
  const { uuid: batchUUID } = useParams();
  const {
    destinationContainer,
  } = Object.fromEntries(searchParams.entries()) ?? {};
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const { isLoading, setLoading } = useActionLoadingStore();
  const [pageState, setPageState] = useState({
    id: batchUUID,
    batch: null,
    containers: [],
    byContainers: false,
  });

  const [selectedContainers, setSelectedContainers] = useState([]);
  const [availableContainers, setAvailableContainers] = useState(pageState.containers);
  const [nonEmptyContainersByUri, setNonEmptyContainersByUri] = useState({});

  const { openActionPanel, closeActionPanel, closeAllActionPanels } = useActionPanelStore();
  const { features } = useContext(FeaturesContext);
  const isEnforceContainersEnabled = isFeatureEnabled(features, FEATURES.ENFORCE_CONTAINERS);

  const navigate = useNavigate();
  const { addToast } = useToasts();

  const reduceQuantityByContainers = (containerList) => containerList.reduce((total, uuid) => {
    return total + nonEmptyContainersByUri[uuid]?.quantity;
  }, 0);

  const { quantity: convertedBatchQuantity, units: convertedBatchUnits, isConverted } =
    useMemo(() => {
      if (pageState.batch) {
        return convertToUserUnits(pageState.batch.quantity, pageState.batch.units);
      }
      return { quantity: '0.00', units: '', isConverted: false };
    }, [pageState.batch]);

  const {
    selectedAmountBase,
    selectedAmountConverted,
    handleSelectedAmountChange,
    handleRangeChange,
  } = useSelectedAmountHandler(
    pageState.batch?.quantity || 0,
    pageState.batch?.units || '',
    convertedBatchQuantity,
    convertedBatchUnits
  );

  const {
    data: containerData,
    isInitialLoading: isDestinationContainerLoading,
  } = useModule(destinationContainer);

  const {
    data: batchData,
  } = useBatch(pageState.batch?.uri);

  const { data: destinationContainerBatch, isInitialLoading: isDestinationBatchLoading } = useBatch(
    containerData?.current_batch,
    'split-batch-destination'
  );

  const blendCheck = useBlendCheck({
    sourceBatch: pageState.batch,
    destinationBatch: destinationContainerBatch,
    source: blendResource.batch(pageState.batch),
    destination: blendResource.container(containerData),
    actionLabel: 'split',
    // The container resolves first, then its batch - both must land before the
    // absence of a warning can be trusted.
    isLoading: Boolean(destinationContainer) && (isDestinationContainerLoading || isDestinationBatchLoading),
  });

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const batch = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/${batchUUID}/`).json();
      const { EMPTY, ...containerStatusesExceptEmpty } = MATERIAL_CONTAINER_STATUSES;
      const { resources: nonEmptyContainers } = await api.get(`${API_RESOURCES.MATERIAL_CONTAINER}/`, {
        searchParams: {
          'filter[current_batch]': batch.uri,
          'filter[status]': Object.values(containerStatusesExceptEmpty).join(','),
          'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
        },
      }).json();
      nonEmptyContainers.forEach((container) => {
        setNonEmptyContainersByUri((previousState) => ({ ...previousState, [container.uri]: container }));
      });
      setPageState((prevState) => ({
        ...prevState,
        id: batchUUID,
        batch,
        containers: nonEmptyContainers.map((nonEmptyContainer) => nonEmptyContainer.uri),
        byContainers: batch.material_in_containers && nonEmptyContainers.length > 1,
      }));
      setAvailableContainers(nonEmptyContainers.map((nonEmptyContainer) => nonEmptyContainer.uri));
    } catch (error) {
      if (error.name === 'HTTPError') {
        setPageState((prevState) => ({
          ...prevState,
          id: batchUUID,
          error: {
            name: error.name,
            status: error.response && error.response.status,
          },
        }));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, [batchUUID]);

  const onActionRelocate = async (resourceToRelocate, resourceToRelocateBatchUri) => {
    try {
      const locationToRelocate = batchData.location;
      const subLocationToRelocate = batchData.sub_location;
      await relocateContainerAction(
        locationToRelocate,
        subLocationToRelocate,
        resourceToRelocate,
        resourceToRelocateBatchUri
      );
      closeActionPanel();
    } catch (error_) {
      console.error(error_);
      throw error_;
    }
  };

  const onConfirmRelocate = async (resolve) => {
    try {
      await onActionRelocate(containerData.uri, containerData.current_batch);
      closeAllActionPanels();
      resolve(true);
    } catch (error) {
      addToast(`${error}`, { appearance: 'error' });
      closeActionPanel();
      resolve(false);
    }
  };

  const onCancelRelocate = async (resolve) => {
    closeAllActionPanels();
    resolve(false);
  }

  const checkLocationMismatch = async () => {
    return new Promise((resolve) => {
      const hasDifferentLocations = hasLocationsMismatch(
        containerData.location,
        batchData.location
      );

      if (!hasDifferentLocations) {
        resolve(true);
        return;
      }

      openActionPanel({
        panelId: 'relocation-panel',
        title: 'Locations Mismatch',
        stack: true,
        content: (
          <LocationsMismatchWarning
            sourceResource={batchData}
            destinationResource={containerData}
            sourceType="Material Batch"
            destinationType="Permanent Container"
            sourceDisplayName={batchData.name}
            action="Split Batch"
            bottomTextRenderer={() => (
              <p>Would you like to relocate the permanent Container to match the Material Batch location?</p>
            )}
            onConfirm={() => onConfirmRelocate(resolve)}
            onCancel={() => onCancelRelocate(resolve)}
          />
        ),
      });
    });
  };

  const handleSplitBatchAction = async (batchUri) => {
    let actionResult;
    setSubmitting(true);

    try {
      if (isEnforceContainersEnabled) {
        if (pageState.containers.length > 1) {
          const containersToSplit = selectedContainers.map(uri => ({ uri }));
          actionResult = await loadMultipleContainersIntoDestinationAction(
            containersToSplit,
            batchUri,
            destinationContainer,
          );
        } else {
          await partialLoadContainerAction(
            pageState.containers[0],
            batchUri,
            destinationContainer,
            selectedAmountBase
          );
        }
      } else {
        /* eslint-disable camelcase */
        const payload = {
          metadata: {},
          action_type: MATERIAL_BATCH_ACTIONS.SPLIT_BATCH,
          source_batch: batchUri,
        };

        if (pageState.byContainers) {
          payload.metadata.containers_to_split = selectedContainers.map(uri => ({ uri }));
        } else {
          payload.metadata.amount_to_split = selectedAmountBase;
        }
        /* eslint-enable camelcase */

        actionResult = await api
          .post(`${API_RESOURCES.MATERIAL_BATCH_ACTION}/`, { json: payload })
          .json();
      }
    } catch (error_) {
      const { errors } = await error_.response.json();
      setSubmitError(errors[0].title);
      setSubmitting(false);
      return;
    }

    const isMultipleContainers = availableContainers.length > 1;
    const initialBatchQuantity = pageState.batch.quantity - selectedAmountBase;
    const initialBatchUUID = getUuid(pageState.batch.uri);

    const baseRouteParams = {
      uuid: getUuid(
        isEnforceContainersEnabled
          ? (isMultipleContainers
            ? actionResult.metadata.split_off_batch
            : pageState.batch.uri)
          : actionResult.metadata.split_off_batch
      ),
    };

    const baseQueryParams = {
      action: isEnforceContainersEnabled
        ? MATERIAL_BATCH_ACTIONS.SPLIT_BATCH_CONTAINER
        : MATERIAL_BATCH_ACTIONS.SPLIT_BATCH,
      initialBatchQuantity,
      initialBatchUUID,
      ...(isEnforceContainersEnabled && {
        destinationContainerUUID: getUuid(destinationContainer),
      }),
      ...(isEnforceContainersEnabled && pageState.containers.length === 1 && {
        sourceContainerUUID: getUuid(pageState.containers[0]),
      }),
    };

    navigate(getRouteURI(routes.materialBatchSuccess, baseRouteParams, baseQueryParams));
  }

  const onActionSplitBatch = async (batchUri) => {
    if (isEnforceContainersEnabled) {
      const locationMismatchResolved = await checkLocationMismatch();
      if (!locationMismatchResolved) return;
    }
    await handleSplitBatchAction(batchUri);
  }

  const containerClicked = (containerUuid) => {
    const updatedAvailableContainers = [...availableContainers];
    const updatedSelectedContainers = [...selectedContainers];

    const leftContainerIndex = updatedAvailableContainers.indexOf(containerUuid);
    const selectedContainerIndex = updatedSelectedContainers.indexOf(containerUuid);

    if (leftContainerIndex !== -1) {
      updatedAvailableContainers.splice(leftContainerIndex, 1);
    } else {
      updatedAvailableContainers.push(containerUuid);
    }

    if (selectedContainerIndex !== -1) {
      updatedSelectedContainers.splice(selectedContainerIndex, 1);
    } else {
      updatedSelectedContainers.push(containerUuid);
    }

    setAvailableContainers(updatedAvailableContainers);
    setSelectedContainers(updatedSelectedContainers);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!isLoading && !pageState.batch) {
    return <NotFound id={pageState.id} />;
  }

  const batchQuantity = pageState.batch?.quantity || 0;

  const submitDisabled = isSubmitting ||
    (pageState.byContainers &&
      (!selectedContainers.length || !availableContainers.length)
    ) ||
    (!pageState.byContainers &&
      (!selectedAmountBase || selectedAmountBase === batchQuantity)
    );

  const selectedPercentage = Math.round(selectedAmountBase / batchQuantity * 100) || 0;
  const remainingPercentage = 100 - selectedPercentage;

  const quantityBarMaximumHeight = 100;
  // Height of EMPTY space of bar. If batch is fully loaded, it's 0 value
  const sourceBatchAmountBarHeight = (selectedAmountBase / batchQuantity) * quantityBarMaximumHeight;
  const newBatchAmountBarHeight = quantityBarMaximumHeight - sourceBatchAmountBarHeight;

  const formattedRemainingBase = formatThreeDecimalsNumber(batchQuantity - selectedAmountBase);
  const formattedRemainingConverted = formatThreeDecimalsNumber(convertedBatchQuantity - selectedAmountConverted);
  const formattedSelectedBase = formatThreeDecimalsNumber(selectedAmountBase);
  const formattedBatchQuantity = formatThreeDecimalsNumber(batchQuantity);
  const rightColumnTitle = isEnforceContainersEnabled ? containerData?.name : 'New Batch';

  return (
    <ActionPage
      id={pageState.id}
      user={user}
      httpError={pageState.error}
      customErrorText={submitError}
      action={MATERIAL_BATCH_ACTIONS.SPLIT_BATCH}
    >
      <div className="main-content-container">
        <h2 className="fs-mobile-20 mb30">
          Split Batch&nbsp;
          <span className="batch-split-uuid-badge">
            {getShortUuid(pageState.batch.uri)}
          </span>
        </h2>

        <BlendCheckGate check={blendCheck} />

        {pageState.byContainers && (
          <div className="row batch-container-split-list-row">
            <div className="col-xs-6">
              <div className="h5">Batch <span className="batch-split-uuid-badge">{getShortUuid(pageState.batch.uri)}</span>
              </div>
              <div className="h6 header-margin">{availableContainers.length} Container(s)</div>
              <p className="mb0">Total amount of material left</p>
              <ConvertedUnits
                addUnitsParentheses
                className="mb10 d-inline-block"
                quantity={reduceQuantityByContainers(availableContainers)}
                units={pageState.batch?.units}
              />
              <ul className="batch-container-split-list">
                {availableContainers.map((uuid) => {
                  return (
                    <li key={uuid} className="batch-container-split-list-item">
                      <button
                        type="button"
                        disabled={availableContainers.length === 1}
                        className="btn btn-outline-secondary btn-action batch-container-split-list-btn"
                        onClick={() => containerClicked(uuid)}
                      >
                        {getShortUuid(uuid)} {' '}
                        (<ConvertedUnits
                          skipTooltip
                          quantity={nonEmptyContainersByUri[uuid]?.quantity}
                          units={pageState.batch?.units} />)
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="col-xs-6">
              <div className="h5">{rightColumnTitle}</div>
              <div className="h6 header-margin">{selectedContainers.length} Container(s)</div>
              <p className="mb0">Amount of material in new batch</p>
              <ConvertedUnits
                addUnitsParentheses
                className="mb10 d-inline-block"
                quantity={reduceQuantityByContainers(selectedContainers)}
                units={pageState.batch?.units}
              />
              <ul className="batch-container-split-list">
                {selectedContainers.map((uuid) => (
                  <li key={uuid} className="batch-container-split-list-item">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-action batch-container-split-list-btn"
                      onClick={() => containerClicked(uuid)}
                    >
                      {getShortUuid(uuid)}{' '}
                      (<ConvertedUnits
                        skipTooltip
                        quantity={nonEmptyContainersByUri[uuid]?.quantity}
                        units={pageState.batch?.units} />)
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {!pageState.byContainers && (
          <>
            <div className="selectedAmountRow fs-mobile-14 mb30">
              <label className="new-quantity-label text-muted mb0" htmlFor="selectedAmount">
                New batch quantity
              </label>
              <div className="selectedAmountControls">
                <Input
                  id="selectedAmount"
                  name="selectedAmount"
                  min="0"
                  className="selectedAmountInput"
                  step={getInputStep(convertedBatchQuantity)}
                  max={convertedBatchQuantity}
                  type="number"
                  value={selectedAmountConverted}
                  onChange={handleSelectedAmountChange}
                />
                <span className="selectedAmountUnits text-primary">
                  {formatUnits(convertedBatchUnits)} ({selectedPercentage}%)
                </span>
              </div>
            </div>

            <div className="batch-split-section mb30">
              <div className="d-flex justify-content-around align-items-start mb25">
                <div className="text-center batch-split-column">
                  <div className="batch-split-column-header d-flex flex-column align-items-center">
                    <div className="h5 mb0 fw-semibold">Batch</div>
                    <div className="batch-split-column-meta">
                      <span className="batch-split-uuid-badge">
                        {getShortUuid(pageState.batch?.uri)}
                      </span>
                    </div>
                  </div>
                  <div className="batch-material-amount batch-material-amount--compact">
                    <div style={{ paddingTop: `${sourceBatchAmountBarHeight}px`, background: 'white' }} />
                  </div>
                  <div className="h6 mb0 mt10 fw-semibold">
                    {formattedRemainingBase} {formatUnits(pageState.batch.units)}
                  </div>
                  {isConverted && formatConvertedUnits(formattedRemainingConverted, convertedBatchUnits)}
                  <div className="text-muted">{remainingPercentage}%</div>
                </div>
                <div className="text-center batch-split-column">
                  <div className="batch-split-column-header d-flex flex-column align-items-center">
                    <div className="h5 mb0">{rightColumnTitle}</div>
                    <div className="batch-split-column-meta" aria-hidden="true" />
                  </div>
                  <div className="batch-material-amount batch-material-amount--compact">
                    <div style={{ paddingTop: `${newBatchAmountBarHeight}px`, background: 'white' }} />
                  </div>
                  <div className="h6 mb0 mt10 fw-semibold">
                    {formattedSelectedBase} {formatUnits(pageState.batch.units)}
                  </div>
                  {isConverted && formatConvertedUnits(selectedAmountConverted, convertedBatchUnits)}
                  <div className="text-muted">{selectedPercentage}%</div>
                </div>
              </div>

              <div className="text-muted mb15">
                Total: {formattedBatchQuantity} {formatUnits(pageState.batch.units)}
              </div>

              <div className="form-group mb0">
                <RangeInputConversionTooltip
                  visible={isConverted}
                  defaultUnits={pageState.batch?.units}
                  convertedUnits={convertedBatchUnits}
                />
                <input
                  name="selectedAmountRange"
                  className="selectedAmountRange"
                  min="0"
                  step={getInputStep(pageState.batch?.quantity, true)}
                  max={pageState.batch?.quantity}
                  type="range"
                  value={selectedAmountBase}
                  style={{
                    background: `linear-gradient(to right, #007bff 0%, #007bff ${selectedPercentage}%, #e5e7eb ${selectedPercentage}%, #e5e7eb 100%)`,
                  }}
                  onChange={(e) => handleRangeChange(e.target.value)}
                />
                <div className="d-flex justify-content-between text-muted fs-mobile-14">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </>
        )}

        <BlendCheckHint check={blendCheck} />

        <button
          type="submit"
          className="btn btn-lg btn-primary btn-block mt15"
          disabled={submitDisabled || !blendCheck.canProceed}
          onClick={() => blendCheck.canProceed && onActionSplitBatch(pageState.batch.uri)}
        >
          Confirm
        </button>
        <Link to={`/material-container?batch=${pageState.id}`}>
          <button type="button" className="btn btn-default btn-action">
            Cancel
          </button>
        </Link>
      </div>
    </ActionPage>
  );
};

SplitBatch.propTypes = {
  user: userPropType,
};

SplitBatch.defaultProps = {
  user: null,
};

export default SplitBatch;
